import type { RTCIceServerLike, SignalData } from '../../shared/protocol';
import { fallbackIceServers, fastIceServers } from '../lib/ice';

/** Without a connection after this long, TURN over TCP/TLS is added (see lib/ice.ts). */
const FALLBACK_AFTER_MS = 6_000;

// Verbose WebRTC tracing, enabled with localStorage['nexo.debug'] = '1'.
const DEBUG = (() => {
  try {
    return localStorage.getItem('nexo.debug') === '1';
  } catch {
    return false;
  }
})();

export type Quality = 'good' | 'fair' | 'poor' | 'unknown';

export interface PeerStats {
  state: RTCPeerConnectionState;
  quality: Quality;
  rttMs: number | null;
  lossPct: number | null;
}

interface PeerEvents {
  signal(data: SignalData): void;
  track(stream: MediaStream): void;
  stats(stats: PeerStats): void;
}

export interface LocalMedia {
  audio: MediaStreamTrack | null;
  video: MediaStreamTrack | null;
  screen: MediaStreamTrack | null;
  /** Sound of the shared tab/screen, sent alongside the screen video. */
  screenAudio: MediaStreamTrack | null;
  cameraCarrier: MediaStream;
  screenCarrier: MediaStream;
}

/**
 * One RTCPeerConnection to one remote participant, using the W3C "perfect negotiation"
 * pattern so both sides can renegotiate (screen share, ICE restarts) without glare.
 *
 * Every local track goes out on its own sendonly transceiver tagged with a stable carrier
 * stream: the camera carrier (mic + camera) and the screen carrier. The remote side tells
 * them apart by stream id, which each participant publishes through the signaling server.
 */
export class Peer {
  readonly pc: RTCPeerConnection;
  private makingOffer = false;
  private ignoreOffer = false;
  private settingRemoteAnswer = false;
  private audioTx: RTCRtpTransceiver | null = null;
  private videoTx: RTCRtpTransceiver | null = null;
  /** Local tracks not yet attached (polite side waits for the first remote offer). */
  private pending: LocalMedia | null;
  private attachTimer: ReturnType<typeof setTimeout> | null = null;
  private screenTx: RTCRtpTransceiver | null = null;
  private screenAudioTx: RTCRtpTransceiver | null = null;
  private statsTimer: ReturnType<typeof setInterval>;
  private disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** ICE list with TCP/TLS TURN, kept back until the fast one fails to connect. */
  private fallbackServers: RTCIceServerLike[] | null = null;
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private lastInbound = { lost: 0, received: 0 };
  private closed = false;
  private bitrates = { camera: 600, screen: 1500 };

  constructor(
    readonly remoteId: string,
    private readonly polite: boolean,
    iceServers: RTCIceServerLike[],
    local: LocalMedia,
    private readonly events: PeerEvents,
  ) {
    this.pc = new RTCPeerConnection({ iceServers: fastIceServers(iceServers) as RTCIceServer[], bundlePolicy: 'max-bundle' });
    this.fallbackServers = fallbackIceServers(iceServers);
    if (this.fallbackServers) {
      this.fallbackTimer = setTimeout(() => this.pc.connectionState !== 'connected' && this.useFallback(), FALLBACK_AFTER_MS);
    }

    // Only the impolite side opens the first negotiation. If both sides offered at once, the
    // polite one would roll back an offer whose ICE candidates were already sent and dropped,
    // and Chrome does not gather them again, leaving ICE stuck in "new". The polite side
    // attaches its tracks right after answering (or after a short fallback delay).
    this.pending = local;
    if (polite) this.attachTimer = setTimeout(() => this.attachLocal(), 3000);
    else this.attachLocal();

    this.pc.onnegotiationneeded = async () => {
      this.log('negotiationneeded', this.pc.signalingState);
      try {
        this.makingOffer = true;
        await this.pc.setLocalDescription();
        this.sendDescription();
      } catch (err) {
        console.warn('[peer] negociación fallida', remoteId, err);
      } finally {
        this.makingOffer = false;
      }
    };
    this.pc.onicecandidate = ({ candidate }) => {
      if (!candidate) return this.events.signal({ candidate: null });
      const c = candidate.toJSON();
      this.events.signal({ candidate: { candidate: c.candidate ?? '', sdpMid: c.sdpMid, sdpMLineIndex: c.sdpMLineIndex, usernameFragment: c.usernameFragment } });
    };
    this.pc.ontrack = ({ streams }) => {
      // Only tracks tagged with a carrier stream are real media from the other side.
      if (streams[0]) this.events.track(streams[0]);
    };
    this.pc.onconnectionstatechange = () => this.onConnectionState();
    this.pc.onsignalingstatechange = () => this.log('signaling', this.pc.signalingState);
    this.pc.oniceconnectionstatechange = () => this.log('ice', this.pc.iceConnectionState);

    this.statsTimer = setInterval(() => void this.collectStats(), 3000);
  }

  private attachLocal(): void {
    const local = this.pending;
    if (!local || this.closed) return;
    this.pending = null;
    if (this.attachTimer) clearTimeout(this.attachTimer);
    this.attachTimer = null;
    this.log('attach local tracks');
    this.audioTx = this.pc.addTransceiver(local.audio ?? 'audio', { direction: 'sendonly', streams: [local.cameraCarrier] });
    this.videoTx = this.pc.addTransceiver(local.video ?? 'video', { direction: 'sendonly', streams: [local.cameraCarrier] });
    if (local.screen) this.setScreen(local.screen, local.screenCarrier);
    if (local.screenAudio) this.setScreenAudio(local.screenAudio, local.screenCarrier);
  }

  private log(...args: unknown[]): void {
    if (DEBUG) console.debug(`[peer ${this.remoteId}${this.polite ? ' polite' : ''}]`, ...args);
  }

  private sendDescription(): void {
    const d = this.pc.localDescription;
    if (d) this.events.signal({ description: { type: d.type, sdp: d.sdp } });
  }

  private onConnectionState(): void {
    const state = this.pc.connectionState;
    if (this.disconnectTimer) clearTimeout(this.disconnectTimer);
    this.disconnectTimer = null;
    if (state === 'connected' && this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
    if (state === 'failed' && !this.useFallback()) this.pc.restartIce();
    if (state === 'disconnected') {
      // Brief blips recover on their own; restart ICE if the link stays down.
      this.disconnectTimer = setTimeout(() => {
        if (this.pc.connectionState === 'disconnected') this.pc.restartIce();
      }, 4000);
    }
    if (state === 'connected') void this.applyBitrates();
    this.events.stats({ state, quality: 'unknown', rttMs: null, lossPct: null });
  }

  /** Adds TURN over TCP/TLS and restarts ICE (networks that block UDP). Once per connection. */
  private useFallback(): boolean {
    if (!this.fallbackServers || this.closed) return false;
    this.log('ice fallback: TURN TCP/TLS');
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    this.fallbackTimer = null;
    this.pc.setConfiguration({ ...this.pc.getConfiguration(), iceServers: this.fallbackServers as RTCIceServer[] });
    this.fallbackServers = null;
    this.pc.restartIce();
    return true;
  }

  async handleSignal(data: SignalData): Promise<void> {
    if (this.closed) return;
    try {
      if ('description' in data) {
        const description = data.description as RTCSessionDescriptionInit;
        const readyForOffer = !this.makingOffer && (this.pc.signalingState === 'stable' || this.settingRemoteAnswer);
        const offerCollision = description.type === 'offer' && !readyForOffer;
        this.ignoreOffer = !this.polite && offerCollision;
        this.log('recv', description.type, { offerCollision, ignore: this.ignoreOffer, state: this.pc.signalingState });
        if (this.ignoreOffer) return;

        this.settingRemoteAnswer = description.type === 'answer';
        await this.pc.setRemoteDescription(description);
        this.settingRemoteAnswer = false;
        if (description.type === 'offer') {
          await this.pc.setLocalDescription();
          this.sendDescription();
          this.attachLocal();
        }
      } else if (data.candidate) {
        try {
          await this.pc.addIceCandidate(data.candidate);
        } catch (err) {
          if (!this.ignoreOffer) throw err;
        }
      }
    } catch (err) {
      console.warn('[peer] error de señalización', this.remoteId, err);
    }
  }

  async replaceAudio(track: MediaStreamTrack | null): Promise<void> {
    if (this.pending) this.pending.audio = track;
    else await this.audioTx?.sender.replaceTrack(track);
  }

  async replaceVideo(track: MediaStreamTrack | null): Promise<void> {
    if (this.pending) this.pending.video = track;
    else await this.videoTx?.sender.replaceTrack(track);
    void this.applyBitrates();
  }

  setScreen(track: MediaStreamTrack | null, carrier: MediaStream): void {
    if (this.pending) {
      this.pending.screen = track;
      return;
    }
    if (this.screenTx) {
      void this.screenTx.sender.replaceTrack(track);
      void this.applyBitrates();
      return;
    }
    if (!track) return;
    // First share on this connection: adding the transceiver triggers a renegotiation.
    this.screenTx = this.pc.addTransceiver(track, { direction: 'sendonly', streams: [carrier] });
  }

  setScreenAudio(track: MediaStreamTrack | null, carrier: MediaStream): void {
    if (this.pending) {
      this.pending.screenAudio = track;
      return;
    }
    if (this.screenAudioTx) {
      void this.screenAudioTx.sender.replaceTrack(track);
      return;
    }
    if (!track) return;
    this.screenAudioTx = this.pc.addTransceiver(track, { direction: 'sendonly', streams: [carrier] });
  }

  /** Mesh topology: every sender is duplicated per peer, so bitrate drops as the call grows. */
  setBitrates(cameraKbps: number, screenKbps: number): void {
    this.bitrates = { camera: cameraKbps, screen: screenKbps };
    void this.applyBitrates();
  }

  private async applyBitrates(): Promise<void> {
    const apply = async (sender: RTCRtpSender | undefined, kbps: number) => {
      if (!sender?.track) return;
      const params = sender.getParameters();
      if (!params.encodings?.length) return;
      params.encodings[0].maxBitrate = kbps * 1000;
      try {
        await sender.setParameters(params);
      } catch {
        /* not negotiated yet; retried on the next connection change */
      }
    };
    await apply(this.videoTx?.sender, this.bitrates.camera);
    await apply(this.screenTx?.sender, this.bitrates.screen);
  }

  private async collectStats(): Promise<void> {
    if (this.closed || this.pc.connectionState !== 'connected') return;
    let rtt: number | null = null;
    let lost = 0;
    let received = 0;
    const report = await this.pc.getStats();
    report.forEach((s) => {
      if (s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded' && typeof s.currentRoundTripTime === 'number') {
        rtt = s.currentRoundTripTime * 1000;
      }
      if (s.type === 'inbound-rtp') {
        lost += s.packetsLost ?? 0;
        received += s.packetsReceived ?? 0;
      }
    });
    const dLost = Math.max(0, lost - this.lastInbound.lost);
    const dRecv = Math.max(0, received - this.lastInbound.received);
    this.lastInbound = { lost, received };
    const lossPct = dLost + dRecv > 0 ? (dLost / (dLost + dRecv)) * 100 : 0;
    const r = rtt ?? 0;
    const quality: Quality = r < 300 && lossPct < 2 ? 'good' : r < 600 && lossPct < 8 ? 'fair' : 'poor';
    this.events.stats({ state: this.pc.connectionState, quality, rttMs: rtt, lossPct });
  }

  close(): void {
    this.closed = true;
    clearInterval(this.statsTimer);
    if (this.attachTimer) clearTimeout(this.attachTimer);
    if (this.disconnectTimer) clearTimeout(this.disconnectTimer);
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    this.pc.onnegotiationneeded = null;
    this.pc.onicecandidate = null;
    this.pc.ontrack = null;
    this.pc.onconnectionstatechange = null;
    this.pc.close();
  }
}
